import {
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChildren,
    ElementRef,
    inject,
    input,
    InputSignal,
    OnInit,
    output,
    OutputEmitterRef,
    signal,
    Signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {ResponsiveDirective} from "../../responsive/responsive.directive";
import {ResponsiveService} from "../../responsive/responsive.service";
import {NavigationService} from "../../navigation/navigation.service";
import {Page, PageAlignment, PageLayout} from "../../navigation/page";
import {ContentMessageComponent} from "../../notification/message/content-message.component";
import {FooterMessagesComponent} from "../../notification/footer-messages/footer-messages.component";
import {IconButtonComponent} from "../../buttons/icon-button/icon-button.component";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ChewzieAttributionComponent} from "../../ui-elements/chewzie-attribution/chewzie-attribution.component";
import {LogoComponent} from "../../ui-elements/logo/logo.component";
import {LegalPanelComponent} from '../../legal/legal-panel/legal-panel.component';
import {BackgroundImageDirective} from '../../ui-elements/image/background-image.directive';
import {AppHeaderPlacement, AppNavbarPlacement} from '../../configuration/app-entity.model';
import {CssVarDirective} from '../../utils/css-var.directive';
import {ConfigurationModel} from '../../configuration/configuration.model';

/**
 * Desktop home page component.
 * This is responsible for the consistent layout of a desktop home page.
 * It provides standard content including headers, footers, and sidebar.
 * It also manages the scrolling for the page.
 */
@Component({
    selector: "woa-desktop-home-page-layout",
    templateUrl: "./desktop-home-page-layout.component.html",
    styleUrls: ["./desktop-home-page-layout.component.scss", "../../ui-elements/image/background-image.scss"],
    providers: [{provide: PageLayout, useExisting: DesktopHomePageLayoutComponent}],
    imports: [
        BackgroundImageDirective,
        ChewzieAttributionComponent,
        CssVarDirective,
        FooterMessagesComponent,
        IconButtonComponent,
        LegalPanelComponent,
        LogoComponent,
        NgClass,
        ResponsiveDirective,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DesktopHomePageLayoutComponent extends PageLayout implements OnInit {
    public page: Page = inject(Page);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public navigationService: NavigationService = inject(NavigationService);
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    private responsiveService: ResponsiveService = inject(ResponsiveService);

    PageAlignment = PageAlignment;
    IconButtonType = IconButtonType;
    AppHeaderPlacement = AppHeaderPlacement;
    AppNavbarPlacement = AppNavbarPlacement;

    readonly SIDEBAR_MIN_FLOOR: number = 550;
    readonly SIDEBAR_MIN_CEILING: number = 768;

    public readonly enableNavMenuIcon: InputSignal<boolean> = input<boolean>(false);

    public onRefresh: OutputEmitterRef<void> = output<void>();
    public onSearchMenu: OutputEmitterRef<void> = output<void>();

    public scrollerER: Signal<ElementRef | undefined> = viewChild("scroller");
    public containerER: Signal<ElementRef | undefined> = viewChild("container");
    public fullWidthHeaderNavbarER: Signal<ElementRef | undefined> = viewChild("fullWidthHeaderNavbar");
    public fullWidthHeaderImageER: Signal<ElementRef | undefined> = viewChild("fullWidthHeaderImage");
    public contentER: Signal<ElementRef | undefined> = viewChild("content");
    public contentHeaderTitlebarER: Signal<ElementRef | undefined> = viewChild("contentHeaderTitlebar");
    public contentHeaderNavbarER: Signal<ElementRef | undefined> = viewChild("contentHeaderNavbar");
    public contentHeaderImageER: Signal<ElementRef | undefined> = viewChild("contentHeaderImage");
    public contentBodyER: Signal<ElementRef | undefined> = viewChild("contentBody");
    public contentFooterER: Signal<ElementRef | undefined> = viewChild("contentFooter");
    public sidebarER: Signal<ElementRef | undefined> = viewChild("sidebar");
    public sidebarPlaceholderER: Signal<ElementRef | undefined> = viewChild("sidebarPlaceholder");
    public contentMessages: Signal<ReadonlyArray<ContentMessageComponent>> = contentChildren(ContentMessageComponent);

    public pageScrollerER: WritableSignal<ElementRef | undefined> = signal(undefined);

    public readonly scrollerTop: WritableSignal<number> = signal<number>(0);
    public readonly scrollerScrollbarWidth: WritableSignal<string> = signal<string>("0");
    public readonly contentWidth: WritableSignal<string> = signal<string>("0");
    public readonly sidebarPosition: WritableSignal<string> = signal<string>("fixed");

    public readonly hasHeaderImage: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.app()?.showDesktopHeaderImage && this.orderingContextModel.hasHeaderImage());

    public readonly hasBlankHeaderImage: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.app()?.showDesktopHeaderImage && !this.orderingContextModel.hasHeaderImage());

    public readonly hasNoHeaderImage: Signal<boolean> = computed<boolean>(() =>
        !this.configurationModel.app()?.showDesktopHeaderImage);

    public readonly showContentHeaderTitlebar: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.app()?.navbarPlacement === AppNavbarPlacement.SIDEBAR && !this.configurationModel.app()?.showDesktopHeaderImage && this.configurationModel.app()?.showDesktopHeaderLogo);

    public readonly headerImageHasNavbar: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.app()?.navbarPlacement === AppNavbarPlacement.PAGE_HEADER);

    public readonly showHeaderImageTitleLogo: Signal<boolean> = computed<boolean>(() =>
        !this.headerImageHasNavbar() && this.configurationModel.app()?.showDesktopHeaderLogo);

    public readonly showSidebarNavbar: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.app()?.navbarPlacement === AppNavbarPlacement.SIDEBAR);

    private sidebarLastTouchY: number = undefined;
    private forcedScrolling: boolean = false;

    public readonly contentStickyTop: Signal<(content: HTMLElement) => string> = computed<(content: HTMLElement) => string>(() => {
        const contentHeaderNavbarER: ElementRef = this.contentHeaderNavbarER();
        const fullWidthHeaderNavbarER: ElementRef = this.fullWidthHeaderNavbarER();
        const scrollerER: ElementRef = this.scrollerER();
        return (content: HTMLElement) => {
            const navbarHeight: number = contentHeaderNavbarER ? contentHeaderNavbarER.nativeElement.offsetHeight
                : fullWidthHeaderNavbarER ? fullWidthHeaderNavbarER.nativeElement.offsetHeight
                    : 0;
            return scrollerER ? Math.min(scrollerER.nativeElement.clientHeight - content.offsetHeight, navbarHeight) + "px" : undefined;
        }
    });

    public readonly navbarHeight: Signal<number> = computed(() =>
        Math.max(this.contentHeaderNavbarER()?.nativeElement.offsetHeight ?? 0, this.fullWidthHeaderNavbarER()?.nativeElement.offsetHeight ?? 0));

    ngOnInit(): void {
        this.page.containerComponentRef && this.page.containerComponentRef.instance.geometry.set({alignment: PageAlignment.FULL});
    }

    public scrollByWheel(event: WheelEvent): void {
        if (this.scrollerER() && event.deltaY) {
            const deltaY: number = this.responsiveService.isFirefox() ? event.deltaY * 16 : event.deltaY / 2;
            this.scrollerER().nativeElement.scrollBy(0, deltaY);
        }
    }

    public scrollByTouchStart(event: TouchEvent): void {
        this.sidebarLastTouchY = event.changedTouches[0].clientY;
    }

    public scrollByTouchMove(event: TouchEvent): void {
        if (this.sidebarLastTouchY != undefined) {
            this.scrollerER().nativeElement.scrollBy(0, this.sidebarLastTouchY - event.changedTouches[0].clientY);
        }
        this.sidebarLastTouchY = event.changedTouches[0].clientY;
    }

    /**
     * Keeps the sidebar in the correct position when the page scrolls.
     * This is a little like the CSS "sticky" position, except that with "sticky", the sidebar would not scroll until
     * it reached the bottom of the page. In contrast, this approach has the sidebar scrolling immediately and then stopping
     * once the bottom is visible.
     */
    public scrolling(): void {
        if (!this.forcedScrolling) {
            this.positionSidebar();
        }
    }

    public positionSidebar(): void {
        if (!this.scrollerER() || !this.sidebarER() || !this.containerER()) {
            return;
        }

        const scrollerHeight: number = this.scrollerER().nativeElement.clientHeight;
        const containerTop: number = this.containerER().nativeElement.getBoundingClientRect().top - this.scrollerER().nativeElement.getBoundingClientRect().top;
        const topClearance: number = this.fullWidthHeaderNavbarER() ? this.fullWidthHeaderNavbarER().nativeElement.getBoundingClientRect().bottom : 0;
        const contentTopClearance: number = topClearance + (this.contentHeaderNavbarER() ? this.contentHeaderNavbarER().nativeElement.getBoundingClientRect().bottom : 0);
        const footerHeight: number = this.contentFooterER() ? this.contentFooterER().nativeElement.clientHeight : 0;
        const titleBarHeight: number = this.contentHeaderTitlebarER() ? this.contentHeaderTitlebarER().nativeElement.clientHeight : 0;
        const topStop: number = Math.max(containerTop, topClearance);
        const availableHeight: number = scrollerHeight - topStop;
        const useAbsolutePositioning: boolean = !!this.fullWidthHeaderImageER();

        // The sidebar cannot be smaller than the minimum floor, but must always be at least high enough to fill the page.
        this.sidebarER().nativeElement.style.minHeight = Math.max(this.SIDEBAR_MIN_FLOOR, availableHeight) + "px";

        // Try to limit the sidebar height to the page less the height of the full width navbar (if shown), but only down to the minimum ceiling (which matches the height of a "short" page).
        this.sidebarER().nativeElement.style.maxHeight = Math.max(this.SIDEBAR_MIN_CEILING - topClearance, this.responsiveService.availableHeight() - topClearance) + "px";

        // Check the required height here as it will be derived from the max and min heights just calculated.
        const requiredHeight: number = this.sidebarER().nativeElement.clientHeight;

        // If the sidebar fits withing the scroller:
        // position it at the top of the layout container.
        // Otherwise:
        // position it to scroll with its parent until it no longer extends past the bottom of the scroller, then fix it in that position.
        //
        // Note: if there is an image header, and the sidebar is scrolling with its parent, using fixed position creates a jittering effect
        // on firefox due to that browser's implementation of asynchronous scrolling, where scrolling is not done in the main JavaScript thread,
        // and so can move the static/absolute positioned objects before this event handler is called. So for that case, we use absolute
        // positioning instead. This can in turn produce some "bottom-jitter" as the height of the panel increases to keep up with the scrolling,
        // but it is less noticeable than "top-jitter".
        // Absolute positioning also affects horizontal positioning where overlay scrolling is not in use.
        if (requiredHeight <= availableHeight) {
            if (useAbsolutePositioning && (containerTop > topClearance)) {
                DesktopHomePageLayoutComponent.positionAbsolute(this.sidebarER().nativeElement, 0);
            } else {
                DesktopHomePageLayoutComponent.positionFixed(this.sidebarER().nativeElement, topStop);
            }
        } else if (containerTop + requiredHeight >= availableHeight + topClearance) {
            if (useAbsolutePositioning) {
                DesktopHomePageLayoutComponent.positionAbsolute(this.sidebarER().nativeElement, 0);
            } else {
                DesktopHomePageLayoutComponent.positionFixed(this.sidebarER().nativeElement, containerTop);
            }
        } else {
            DesktopHomePageLayoutComponent.positionFixed(this.sidebarER().nativeElement, availableHeight - requiredHeight + topClearance);
        }

        // Adjust the height of the placeholder to ensure the page stretches to accommodate the actual height of the sidebar.
        if (this.sidebarPlaceholderER()) {
            this.sidebarPlaceholderER().nativeElement.style.height = requiredHeight + "px";
        }

        if (this.responsiveService.isIos()) {
            this.forceRepaint();
        }

        // A full-width header image restricts the height of the sidebar, so we want to force enough content height
        // below the header to allow the header to be fully scrolled off of the screen.
        // Even for a content header image (not extending over the sidebar), we will implement the same behaviour when
        // there is also a header navbar. Otherwise, a short menu would not allow the page to be scrolled, and the navbar opacity would never change.
        // Since we don't have a floating content footer, this also means that in this mode (content header image),
        // the footer will only ever be fully visible when the header image is fully scrolled.
        // While that is not technically required for a content header, since a scroll amount of one pixel would suffice,
        // we will keep the behaviour consistent.
        // This also ensures that when fully scrolled, we don't end up with half an image visible below the navbar.
        if ((this.fullWidthHeaderImageER() || (this.contentHeaderImageER() && this.contentHeaderNavbarER())) && this.contentBodyER()) {
            this.contentBodyER().nativeElement.style.minHeight = scrollerHeight - contentTopClearance - titleBarHeight - footerHeight + "px";
        }

        // Keep track of the scroller top value. This is used to set element classes for detection of scrolling (e.g. the navbar opacity).
        this.scrollerTop.set(this.scrollerER().nativeElement.scrollTop);

        // Keep track of the scrollbar width and sidebar position. These are set as a CSS property and used to compensate for differences in
        // right-side positioning relative to the scrollbar between absolute and fixed positioning.
        this.scrollerScrollbarWidth.set((this.scrollerER().nativeElement.offsetWidth - this.scrollerER().nativeElement.clientWidth) + "px");
        this.sidebarPosition.set(this.sidebarER().nativeElement.style.position);

        // Keep track of the content width. This is set as a CSS property and used for calculating gutter widths.
        if (this.contentER()) {
            this.contentWidth.set(this.contentER().nativeElement.offsetWidth + "px");
        }

        setTimeout(() => this.checkActiveElement());
    }

    private static positionFixed(element: HTMLElement, top: number) {
        element.style.position = "fixed";
        element.style.top = top + "px";
    }

    private static positionAbsolute(element: HTMLElement, top: number) {
        element.style.position = "absolute";
        element.style.top = top + "px";
    }

    private forceRepaint() {
        this.sidebarER().nativeElement.style.zIndex = this.sidebarER().nativeElement.style.zIndex % 2 + 1;
        this.sidebarER().nativeElement.style.transform = "translateZ(-" + this.sidebarER().nativeElement.style.zIndex + "px)";
    }

    /**
     * Check that we haven't scrolled the active field out of view when the on-screen keyboard pops up.
     * If we have, scroll it into view, but disable our response to scrolling while that is happening.
     */
    private checkActiveElement(): void {
        if (document.activeElement && this.responsiveService.isKeyboardOpen()) {
            this.forcedScrolling = true;
            this.responsiveService.scrollActiveElementIntoView();
            setTimeout(() => this.forcedScrolling = false, 500);
        }
    }
}
